import * as React from 'react';
import { Trash2 } from 'lucide-react';
import { SymbolType, SystemType } from '@shared/types/tsk';
import TradeIcon from '../TradeIcon';
import { SYSTEM_COLORS } from '../../utils/drawing';

interface SelectionMenuProps {
    floatingPos: { x: number; y: number } | null;
    selectionType: 'symbol' | 'node';
    currentType?: SymbolType;
    currentSystem: SystemType;
    availableFixtures: SymbolType[];
    availableFittings: SymbolType[];
    availableDirections: SymbolType[];
    onTypeChange: (type: SymbolType) => void;
    onSystemChange: (system: SystemType) => void;
    onDelete: () => void;
    onStopBubbling: (e: React.PointerEvent | React.MouseEvent) => void;
}

const SelectionMenu: React.FC<SelectionMenuProps> = ({
    floatingPos, selectionType, currentType, currentSystem,
    availableFixtures, availableFittings, availableDirections,
    onTypeChange, onSystemChange, onDelete, onStopBubbling
}) => {
    if (!floatingPos) return null;

    const relevantTypes = availableFixtures.includes(currentType!)
        ? availableFixtures
        : availableFittings.includes(currentType!)
            ? availableFittings
            : availableDirections;

    return (
        <div
            className="absolute flex flex-col items-center gap-2 floating-ui pointer-events-auto"
            style={{ left: floatingPos.x, top: floatingPos.y - 160, transform: 'translateX(-50%)' }}
            onPointerDown={onStopBubbling}
        >
            {selectionType === 'symbol' && (
                <div className="bg-slate-900/95 backdrop-blur-md border border-white/20 rounded-[1.5rem] p-1.5 shadow-2xl flex gap-1 max-w-[280px] overflow-x-auto no-scrollbar ring-1 ring-black">
                    {relevantTypes.map(type => (
                        <button
                            key={type}
                            onClick={() => onTypeChange(type)}
                            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all shrink-0 ${currentType === type ? 'bg-white/10 ring-1 ring-white/30' : 'hover:bg-white/5 opacity-40 hover:opacity-100'}`}
                        >
                            <TradeIcon type={type} system={currentSystem} size={22} />
                        </button>
                    ))}
                </div>
            )}
            <div className="bg-slate-900/95 backdrop-blur-md border border-white/20 rounded-full px-2 py-1.5 shadow-2xl flex items-center gap-2 ring-1 ring-black">
                {Object.entries(SYSTEM_COLORS).map(([sys, color]) => (
                    <button
                        key={sys}
                        onClick={() => onSystemChange(sys as SystemType)}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${currentSystem === sys ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'}`}
                        style={{ backgroundColor: color }}
                    />
                ))}
                <div className="w-px h-6 bg-white/10 mx-1" />
                <button
                    onClick={onDelete}
                    className="p-1.5 text-red-400 hover:text-red-300 transition-colors bg-white/5 rounded-full hover:bg-white/10"
                >
                    <Trash2 size={18} />
                </button>
            </div>
        </div>
    );
};

export default SelectionMenu;
